import React, { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async"; // <--- Added Helmet Import
import logo from "./assets/logo.png"; // logo image path
import frontimage from "./assets/frontimage.jpg"; // front image path
import footerLogo from "./assets/footer-logo.png";

export default function MentorshipAcademy() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  const quotes = [
    "Growth accelerates when wisdom replaces urgency.",
    "Purpose is sustained by structure, not enthusiasm.",
    "God commits influence to those who can steward order.",
    "Visibility is not validation.",
    "Mentorship confronts who you are before it celebrates what you can do.",
    "Every limitation you accept was first learned.",
    "Transformation begins when you grieve the gap between potential and reality.",
    "Consistency is proof that discipline has taken root.",
    "Calling without character becomes noise.",
    "Alignment precedes acceleration.",
    "What you refuse to master will eventually master you.",
    "Destiny responds to preparation, not desire.",
  ];

  const curriculum = [
    {
      title: "Crisis leadership & resilience ",
      content:
        "Prepares leaders for calm, rapid decision‑making and reputation management in crises, with trauma‑informed considerations; deliverable is a crisis playbook for the first 24 hours and first 30 days.",
    },
    {
      title: "Self‑awareness, values, character & integrity",
      content:
        "Deepens values‑driven leadership and moral courage, explores bias and the shadow side, and yields a values hierarchy and decision “non‑negotiables.”",
    },
    {
      title: "Emotional intelligence & relational maturity ",
      content:
        "Builds EQ for influence, healthy boundaries, and repair skills; participants create a relationship strategy for three key stakeholders.",
    },
    {
      title: "Leadership presence & executive communication",
      content:
        "Develops executive presence, credibility, and strategic storytelling with tools like the message house; deliverable is a recorded leadership brief with feedback.",
    },
    {
      title: "Strategic thinking & systems view",
      content:
        "Introduces systems thinking, second‑order effects, and leverage points; participants map their organization with a system map.",
    },
    {
      title: "Vision, mission & strategic positioning",
      content:
        "Clarifies vision and differentiation, especially for public/religious leaders, using SWOT/TOWS and value theory; produces a revised vision statement and strategic anchors.",
    },
    {
      title: "Decision‑making under uncertainty",
      content:
        "Teaches probabilistic thinking, scenario planning, and cognitive traps (e.g., overconfidence); outputs include three future scenarios and decision criteria.",
    },
    {
      title: "Strategic priorities & resource focus",
      content:
        "Focuses on trade‑offs and prioritization using OKRs/OGSM and impact‑effort analysis; participants define three strategic priorities and draft OKRs.",
    },
    {
      title: "Transformational leadership & change psychology",
      content:
        "Covers leading change versus managing change, resistance as data, and classic change frameworks; deliverable is a change readiness assessment.",
    },
    {
      title: "Culture engineering",
      content:
        "Treats culture as behaviours and beliefs to be shaped through audits, rituals, and reinforcement; produces a culture shift plan (behaviours to install/remove).",
    },
    {
      title: "Interactive Sessions",
      content:
        "Engage in dynamic discussions, Q&A, and practical applications of mentorship principles.",
    },
    {
      title: "And so much more...",
      content:
        "Additional transformative sessions designed to propel you into your divine assignment.",
    },
  ];

  const wisdomQuotes = [
    "Growth becomes inevitable when humility meets instruction.",
    "Purpose does not announce itself; it is revealed through obedience.",
    "Anointing opens doors, but only discipline keeps them open.",
    "Vision without structure eventually becomes frustration.",
    "Many people call it opposition when it is actually preparation.",
    "Spiritual maturity is proven by responsibility, not revelation.",
    "Influence is sustained by order, not charisma.",
    "What you fail to organize, you will eventually lose.",
    "Leadership begins where excuses end.",
    "God enlarges capacity before He expands territory.",
    "Calling is validated by consistency, not applause.",
    "A future you are unprepared for will always feel delayed.",
    "Transformation is not emotional—it is intentional.",
    "Legacy is built when truth is practiced in private.",
    "Wisdom grows where correction is welcomed.",
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* --- SEO METADATA --- */}
      <Helmet>
        <title>Home | Femi Lanre-Oke Mentorship Academy</title>
        <meta
          name="description"
          content="Welcome to the FLO Mentorship Academy. Empowering individuals to unlock their full potential and influence society through dynamic leadership training."
        />
        <link rel="canonical" href="https://mentorship.femilanreoke.org/" />

        {/* Open Graph for Social Media (WhatsApp/Facebook) */}
        <meta property="og:title" content="Home | FLO Mentorship Academy" />
        <meta
          property="og:description"
          content="Molding dynamic changemakers poised to influence societies and industries."
        />
        <meta
          property="og:image"
          content="https://mentorship.femilanreoke.org/social-preview.jpg"
        />
        <meta
          property="og:url"
          content="https://mentorship.femilanreoke.org/"
        />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Home | FLO Mentorship Academy" />
        <meta
          name="twitter:description"
          content="Molding dynamic changemakers poised to influence societies and industries."
        />
        <meta
          name="twitter:image"
          content="https://mentorship.femilanreoke.org/social-preview.jpg"
        />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-15 h-12 rounded-lg overflow-hidden flex items-center justify-center">
                <img
                  src={logo}
                  alt="FL Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <button
                onClick={() => scrollToSection("about")}
                className="text-gray-700 hover:text-red-600 transition"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("curriculum")}
                className="text-gray-700 hover:text-red-600 transition"
              >
                Curriculum
              </button>
              <button
                onClick={() => scrollToSection("transformation")}
                className="text-gray-700 hover:text-red-600 transition"
              >
                Transformation
              </button>
            </div>

            <div className="hidden md:block">
              <Link
                to="/register"
                className="inline-block bg-gradient-to-r from-red-600 to-gray-800 text-white px-6 py-2 rounded-lg hover:shadow-lg transition"
              >
                Register Now
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection("about")}
                className="block w-full text-left text-gray-700 hover:text-red-600"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("curriculum")}
                className="block w-full text-left text-gray-700 hover:text-red-600"
              >
                Curriculum
              </button>
              <button
                onClick={() => scrollToSection("transformation")}
                className="block w-full text-left text-gray-700 hover:text-red-600"
              >
                Transformation
              </button>
              <Link
                to="/register"
                className="block bg-gradient-to-r from-red-600 to-gray-800 text-white px-6 py-2 rounded-lg w-full text-center"
              >
                Register Now
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-gray-50 via-red-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm text-gray-600 mb-4">
              Raising Leaders For Global Impact
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Femi Lanre-Oke
              <br />
              Mentorship Academy
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto mb-8">
              This mentorship program is a transformational leadership
              experience designed to break the ordinary, rewire your mindset,
              strengthen your convictions, and uncover the purpose you’ve hidden
              under routine, excuses, or fear.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection("register")}
                className="bg-gradient-to-r from-red-600 to-gray-800 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:shadow-xl transition"
              >
                Begin Your Transformation
              </button>
              <button
                onClick={() => scrollToSection("curriculum")}
                className="border-2 border-red-600 text-red-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-red-50 transition"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Rotating Quotes */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quotes.slice(0, 4).map((quote, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-600"
              >
                <p className="text-gray-700 italic text-sm">{quote}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Image Side - Now on LEFT */}
            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src={frontimage}
                  alt="Dr. Femi Lanre-Oke"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Quote Card Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-white mx-4 mb-4 p-6 rounded-lg shadow-lg">
                <p className="text-red-600 text-lg font-semibold italic mb-2">
                  "True mentorship doesn’t just teach skills; it unlocks
                  identity, fuels purpose, and shapes leaders who refuse to be
                  ordinary"
                </p>
                <p className="text-gray-600 text-sm">
                  - Dr. Femi Lanre-Oke(PhD, DMin)
                </p>
              </div>
            </div>

            {/* Text Side - Now on RIGHT */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                More Than a Mentorship Program
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                FLO Mentorship is a divinely inspired leadership formation
                guided by Dr. Femi Lanre-Oke, created for individuals who sense
                that there is more to their calling than what they are currently
                living. This is not a space for hype or temporary inspiration:
                it is a structured journey into clarity, responsibility and
                spiritual maturity.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Within this mentorship, participants are confronted with truth,
                stretched by wisdom and equipped with insight that reshapes how
                they think, decide and lead. FLO Mentorship addresses the root
                of stagnation, dismantling mindsets that keep greatness dormant
                and replacing them with vision, discipline and purpose-driven
                action.
              </p>
              <p className="text-gray-700  mb-4 leading-relaxed">
                Those who pass through this experience do not leave merely
                informed, they emerge redefined. With a deeper understanding of
                God’s intention for their lives, they gain the courage to take
                ownership of their future and the capacity to influence their
                world with conviction, excellence and faith.
              </p>
              <p className="text-gray-700 leading-relaxed">
                FLO Mentorship is for those ready to align with heaven’s
                blueprint and step fully into the leader God has been preparing
                them to become.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-red-600 mb-4">
              What to Expect from the Curriculum
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              FLO Mentorship offers a wisdom driven curriculum that stirs
              conviction and calls for obedience, where alignment begins and
              transformation follows.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {curriculum.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition border border-gray-200"
              >
                <button
                  onClick={() =>
                    setOpenAccordion(openAccordion === index ? null : index)
                  }
                  className="w-full p-6 flex justify-between items-center text-left"
                >
                  <h3 className="text-red-600 font-semibold text-lg">
                    {item.title}
                  </h3>
                  <ChevronDown
                    className={`text-gray-800 transition-transform ${openAccordion === index ? "rotate-180" : ""}`}
                    size={20}
                  />
                </button>

                {openAccordion === index && item.content && (
                  <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-4">
                    {item.content}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-gray-700 italic">
            That’s only the surface. These FLO Mentorship sessions carry truth
            that refuses to leave you comfortable. It presses for obedience and
            when obedience becomes your response, movement begins within you and
            manifests around you.
          </p>
        </div>
      </section>

      {/* Wisdom Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Wisdom from the Sessions
          </h2>
          <p className="text-gray-600 text-center mb-12">
            Excerpts from the Mentorship Academy that will transform your
            perspective
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wisdomQuotes.map((quote, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-red-600 group hover:scale-105"
              >
                <div className="flex items-start gap-4">
                  <div className="text-5xl text-red-600 font-serif leading-none mt-1 opacity-50 group-hover:opacity-100 transition-opacity">
                    "
                  </div>
                  <p className="text-gray-700 leading-relaxed italic text-base pt-2">
                    {quote}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section id="transformation" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-gray-900">
            The Transformation to Expect
          </h2>

          <p className="text-gray-700 text-center max-w-3xl mx-auto mb-12 leading-relaxed">
            If you've ever known what it means to feel stuck, like you're
            moving, but going nowhere, like you've been clapping for others and
            wondering if you have missed your moment, then this academy is for
            you. It will speak to the exact places you thought no one could
            reach, the buried places, the dreams you began to shrink just to
            survive.
          </p>

          <div className="bg-red-50 rounded-2xl p-8 md:p-12 max-w-5xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-red-600 mb-8 text-center">
              Register because
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 leading-relaxed">
                    You're tired of the cycle
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 leading-relaxed">
                    You're ready to stop playing safe with your destiny.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 leading-relaxed">
                    You're ready to sit with truth that may offend your pride
                    but awaken your purpose.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 leading-relaxed">
                    You're ready to let God break the version of you that's been
                    performing and build the version that can actually carry
                    weight.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8 md:p-12 rounded-2xl shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center text-red-400">
                This Is Not For You If...
              </h3>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-red-500 rounded flex items-center justify-center bg-red-500 bg-opacity-20">
                      <svg
                        className="w-4 h-4 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    You are looking for motivation without responsibility.
                  </p>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-red-500 rounded flex items-center justify-center bg-red-500 bg-opacity-20">
                      <svg
                        className="w-4 h-4 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    You want inspiration but resist structure, correction, or
                    accountability.
                  </p>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-red-500 rounded flex items-center justify-center bg-red-500 bg-opacity-20">
                      <svg
                        className="w-4 h-4 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    You prefer comfort over growth and familiarity over
                    transformation.
                  </p>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-red-500 rounded flex items-center justify-center bg-red-500 bg-opacity-20">
                      <svg
                        className="w-4 h-4 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    You are unwilling to examine your thinking, habits, and
                    leadership posture.
                  </p>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-red-500 rounded flex items-center justify-center bg-red-500 bg-opacity-20">
                      <svg
                        className="w-4 h-4 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    You want quick results without submitting to process.
                  </p>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-red-500 rounded flex items-center justify-center bg-red-500 bg-opacity-20">
                      <svg
                        className="w-4 h-4 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    You are more committed to being affirmed than being refined.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-8">
                <div className="bg-red-600 bg-opacity-10 border-l-4 border-red-500 p-6 rounded-lg">
                  <p className="text-white text-base md:text-lg leading-relaxed">
                    <span className="font-bold text-red-400">
                      FLO Mentorship
                    </span>{" "}
                    is for those ready to be shaped, instructed and aligned
                    individuals who understand that lasting impact is formed,
                    not assumed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Register Section */}
      <section
        id="register"
        className="py-20 px-4 bg-gradient-to-br from-red-50 via-gray-50 to-red-100"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Begin Your Transformation?
          </h2>
          <p className="text-gray-700 mb-8">
            Don’t just discover your purpose,master it. Join FLO Mentorship and
            equip yourself for a life of consequence
          </p>

          <Link
            to="/register"
            className="inline-block bg-gradient-to-r from-red-600 to-gray-800 text-white px-12 py-4 rounded-lg text-xl font-semibold hover:shadow-2xl transition transform hover:scale-105"
          >
            Register for FLO Mentorship Now
          </Link>

          <p className="text-gray-600 mt-4 text-sm">
            Limited spaces available • Transform your life today
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={footerLogo}
              alt="FLO Mentorship"
              className="h-16 w-auto drop-shadow-md"
            />
            <p className="text-gray-400 text-sm leading-relaxed max-w-md">
              Empowering individuals to unlock their full potential, molding
              them into dynamic changemakers poised to influence societies and
              industries across borders.
            </p>
          </div>

          <div>
            <h3 className="font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <button
                  onClick={() => scrollToSection("about")}
                  className="hover:text-red-500 transition"
                >
                  About
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("curriculum")}
                  className="hover:text-red-500 transition"
                >
                  Curriculum
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("transformation")}
                  className="hover:text-red-500 transition"
                >
                  Transformation
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold mb-4">Contact</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>Phone: +2348053695097</li>
              <li>Email: info@femilanreoke.org</li>
              <li>Address: Abuja, Nigeria</li>
            </ul>
            <div className="flex space-x-4 text-gray-400 mt-4">
              {/* Facebook */}
              <a
                href="https://web.facebook.com/profile.php?id=100072368183182"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-red-600 transition"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M22 12a10 10 0 1 0-11.5 9.9v-7h-2v-3h2v-2.3c0-2 1.2-3.1 3-3.1.9 0 1.8.1 1.8.1v2h-1c-1 0-1.3.6-1.3 1.2V12h2.3l-.4 3h-1.9v7A10 10 0 0 0 22 12z" />
                </svg>
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/drfemilanreoke"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-red-600 transition"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2c1.7 0 3 1.3 3 3v10c0 1.7-1.3 3-3 3H7c-1.7 0-3-1.3-3-3V7c0-1.7 1.3-3 3-3h10zm-5 3.5A4.5 4.5 0 1 0 16.5 12 4.5 4.5 0 0 0 12 7.5zm0 7.4A2.9 2.9 0 1 1 14.9 12 2.9 2.9 0 0 1 12 14.9zM17.8 6.2a1.1 1.1 0 1 1-1.1-1.1 1.1 1.1 0 0 1 1.1 1.1z" />
                </svg>
              </a>

              {/* YouTube */}
              <a
                href="https://www.youtube.com/@dr.Femilanre-oke"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="hover:text-red-600 transition"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M23.5 6.2s-.2-1.6-.9-2.3c-.8-.9-1.8-.9-2.2-1C17.3 2.5 12 2.5 12 2.5h0s-5.3 0-8.4.4c-.4.1-1.4.1-2.2 1C.7 4.6.5 6.2.5 6.2S0 8.1 0 10v1.9c0 1.9.5 3.8.5 3.8s.2 1.6.9 2.3c.8.9 1.9.8 2.4.9 1.7.2 7.2.4 8.2.4s5.3 0 8.4-.4c.4-.1 1.4-.1 2.2-1 .7-.7.9-2.3.9-2.3s.5-1.9.5-3.8V10c0-1.9-.5-3.8-.5-3.8zM9.5 14.5v-5l5 2.5-5 2.5z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          <p>
            © 2026 Femi Lanre-Oke Mentorship Academy. All rights reserved.
            Powered by{" "}
            <a
              href="https://solidslategroups.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white transition-colors duration-300 underline decoration-gray-600 hover:decoration-white"
            >
              SOLIDSLATE GLOBAL SERVICES LTD
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
